/**
 * Adapt Outwrite task → Apache ECharts option.
 * Supports the `task_type` resource (`given.data`) and legacy flat chart tasks.
 * Visual rules mirror outwrite `scripts/preview_tasks.py` (palette, ticks, projection).
 */

// Fixed order, never cycled. Validated for CVD separation and contrast
// against a light surface (preview_tasks.py PALETTE).
const PALETTE = ['#0072B2', '#D55E00', '#009E73', '#7F3F98'];
const MARKERS = ['circle', 'rect', 'triangle', 'diamond'];

const INK = '#1a1a1a';
const MUTED = '#6b6b6b';
const GRID = '#e4e4e1';
const SURFACE = '#fcfcfb';

function formatCategory(x, isTime) {
  return isTime ? String(Math.round(Number(x))) : String(x);
}

/**
 * Ticks must be the x values the data actually has. Left to itself a
 * plotting library treats years as ordinary numbers and will happily
 * label a tick 2007.5 — there is no such year (preview_tasks.py).
 */
function buildTickIndices(length) {
  if (length <= 0) return [];
  const step = Math.max(1, Math.round(length / 8));
  const indices = [];
  for (let i = 0; i < length; i += step) {
    indices.push(i);
  }
  if (indices[indices.length - 1] !== length - 1) {
    indices.push(length - 1);
  }
  return indices;
}

/**
 * Normalize API task (new envelope or legacy flat) to chart view model.
 * @param {object} task
 * @returns {{
 *   id: string,
 *   chartType: string,
 *   axis: { type?: string, label?: string } | null,
 *   units: string,
 *   tense?: string,
 *   projectedFrom?: number,
 *   series: { label: string, points: [unknown, number][] }[],
 * } | null}
 */
export function normalizeOutwriteChartTask(task) {
  if (!task || typeof task !== 'object') return null;

  if (task.given?.data && typeof task.given.data === 'object') {
    const data = task.given.data;
    const x = Array.isArray(data.x) ? data.x : [];
    const seriesObj =
      data.series && typeof data.series === 'object' && !Array.isArray(data.series)
        ? data.series
        : {};
    const series = Object.entries(seriesObj).map(([label, ys]) => ({
      label,
      points: x.map((xv, i) => [xv, Array.isArray(ys) ? ys[i] : null]),
    }));

    if (!series.length || !x.length) return null;

    const givenType = task.given.type || 'line_chart';
    const chartType =
      givenType === 'line_chart'
        ? 'line'
        : givenType === 'bar_chart'
          ? 'bar'
          : givenType;

    return {
      id: task.id,
      chartType,
      axis: data.axes?.axisX || null,
      units: data.axes?.axisY?.label || '',
      tense: data.tense,
      projectedFrom: data.projectedFrom,
      series,
    };
  }

  if (!Array.isArray(task.series) || !task.series.length) return null;

  return {
    id: task.id,
    chartType: task.chartType || 'line',
    axis: task.axis || null,
    units: task.units || '',
    tense: task.tense,
    projectedFrom: task.projectedFrom,
    series: task.series,
  };
}

/**
 * @param {object} task OutwriteTaskResource | OutwriteChartTask
 * @returns {object | null} ECharts option
 */
export function outwriteTaskToEchartsOption(task) {
  const chart = normalizeOutwriteChartTask(task);
  if (!chart?.series?.length) return null;

  const chartType = chart.chartType || 'line';
  if (chartType !== 'line' && chartType !== 'bar' && chartType !== 'mixed') {
    return buildUnsupportedOption(chart);
  }

  const seriesCount = chart.series.length;
  const isTime = chart.axis?.type === 'time';
  const xs = chart.series[0].points.map((p) => p[0]);
  const categories = xs.map((x) => formatCategory(x, isTime));
  const tickIndices = new Set(buildTickIndices(categories.length));

  const echartsSeries = chart.series.map((s, i) => {
    const colour = PALETTE[i % PALETTE.length];
    const showMarker = s.points.length <= 12;

    return {
      name: s.label,
      type: chartType === 'bar' ? 'bar' : 'line',
      data: s.points.map((p) => p[1]),
      symbol: showMarker ? MARKERS[i % MARKERS.length] : 'none',
      symbolSize: 8,
      showSymbol: showMarker,
      lineStyle: { width: 2, color: colour },
      itemStyle: {
        color: colour,
        borderColor: SURFACE,
        borderWidth: 1.2,
      },
      // Direct label at the line end — secondary encoding (preview_tasks.py)
      endLabel: {
        show: true,
        formatter: s.label,
        color: INK,
        fontSize: 11,
        distance: 6,
      },
      labelLayout: { hideOverlap: true },
      z: 3,
    };
  });

  if (chart.tense === 'mixed' && chart.projectedFrom != null) {
    const projectedLabel = formatCategory(chart.projectedFrom, isTime);
    echartsSeries[0].markLine = {
      silent: true,
      symbol: 'none',
      lineStyle: { color: MUTED, type: 'dashed', width: 1.2 },
      label: {
        show: true,
        formatter: 'projected →',
        color: MUTED,
        fontSize: 11,
        position: 'insideEndTop',
      },
      data: [{ xAxis: projectedLabel }],
    };
  }

  return {
    backgroundColor: SURFACE,
    color: PALETTE,
    title: {
      text: chart.id,
      left: 0,
      top: 0,
      textStyle: { color: INK, fontSize: 14, fontWeight: 600 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
    },
    legend: {
      show: seriesCount >= 2,
      top: 28,
      textStyle: { color: INK, fontSize: 11 },
      icon: 'roundRect',
    },
    grid: {
      left: 48,
      right: 96,
      top: seriesCount >= 2 ? 64 : 48,
      bottom: 48,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories,
      name: chart.axis?.label || '',
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { color: MUTED, fontSize: 12 },
      boundaryGap: chartType === 'bar',
      axisLabel: {
        color: MUTED,
        fontSize: 11,
        interval: 0,
        formatter: (value, index) => (tickIndices.has(index) ? value : ''),
      },
      axisLine: { lineStyle: { color: GRID } },
      axisTick: { alignWithLabel: true },
      splitLine: { show: true, lineStyle: { color: GRID, width: 0.8 } },
    },
    yAxis: {
      type: 'value',
      name: chart.units || '',
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: { color: MUTED, fontSize: 12 },
      axisLabel: { color: MUTED, fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: GRID, width: 0.8 } },
    },
    series: echartsSeries,
  };
}

function buildUnsupportedOption(task) {
  return {
    backgroundColor: SURFACE,
    title: {
      text: `${task.id} (${task.chartType})`,
      subtext: 'Chart type not yet rendered in this view',
      left: 'center',
      top: 'middle',
      textStyle: { color: INK, fontSize: 14 },
      subtextStyle: { color: MUTED, fontSize: 12 },
    },
  };
}
